import React, { useMemo, useRef, useState } from 'react';
import { CheckIcon, FileTextIcon, SearchIcon, XIcon } from 'lucide-react';
import { loans } from '../../data/payPeriods';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/paymentConfig';
import { Field, inputClasses } from './Field';
interface LoanLookupProps {
  value: string | null;
  onChange: (loanId: string | null) => void;
  error?: string;
}
export function LoanLookup({
  value,
  onChange,
  error
}: LoanLookupProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);
  const selected = loans.find(loan => loan.id === value) ?? null;
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return loans.slice(0, 4);
    return loans.filter(loan => [loan.name, loan.borrower, loan.loanNumber].some(field => field.toLowerCase().includes(q)));
  }, [query]);
  if (selected) {
    return <Field id="loanId" label="Loan" required error={error} data-id="element-82" data-mp="rqxllfhc751x">
        <div className="flex items-start gap-3 rounded-md border border-brand-200 bg-brand-50/60 px-3 py-2.5" data-id="element-83" data-mp="wps5yluyd1o5">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white" data-id="element-84" data-mp="11ibirgltfsrw">
            <FileTextIcon className="h-3.5 w-3.5" data-id="element-85" data-mp="16ftq52k6pzxs" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-86" data-mp="8te921vo74ar">
            <p className="truncate text-sm font-semibold text-ink-900" data-id="element-87" data-mp="8upgqcs8kw75">
              {selected.name}
            </p>
            <p className="numeric mt-0.5 truncate text-xs text-ink-500" data-id="element-88" data-mp="iqsfd8nvqcpc">
              {selected.loanNumber} · {selected.borrower} ·{' '}
              {formatCurrency(selected.loanAmount)} · Closed{' '}
              {formatDate(selected.closeDate)}
            </p>
            <p className="numeric mt-1 text-xs font-medium text-brand-700" data-id="element-89" data-mp="1u5o5bqrz80l7">
              Gross commission on loan {formatCurrency(selected.grossCommission)}
            </p>
          </div>
          <button type="button" onClick={() => {
          onChange(null);
          setQuery('');
        }} className="rounded p-1 text-ink-400 transition hover:bg-white hover:text-ink-700 focus:outline-none focus:ring-2 focus:ring-brand-200" aria-label={`Clear selected loan ${selected.name}`} data-id="element-90" data-mp="3v6m07rp08wrt">
            <XIcon className="h-4 w-4" data-id="element-91" data-mp="3bjelfsry9pd7" />
          </button>
        </div>
      </Field>;
  }
  return <Field id="loanId" label="Loan" required error={error} hint="Search by borrower, loan number, or opportunity name." data-id="element-92" data-mp="37jdyfjqsc5uj">
      <div className="relative" onBlur={() => {
      blurTimer.current = window.setTimeout(() => setOpen(false), 120);
    }} onFocus={() => {
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
      setOpen(true);
    }} data-id="element-93" data-mp="3lbu5cvmt4o0q">
        <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" data-id="element-94" data-mp="2nsraszqnglmy" />
        <input id="loanId" type="text" role="combobox" aria-expanded={open} aria-controls="loan-results" aria-autocomplete="list" autoComplete="off" placeholder="Search loans…" value={query} aria-invalid={Boolean(error)} aria-describedby={error ? 'loanId-error' : 'loanId-hint'} onChange={e => {
        setQuery(e.target.value);
        setOpen(true);
      }} className={inputClasses(Boolean(error), 'pl-9')} data-id="element-95" data-mp="1842uk7linl3v" />

        {open && <ul id="loan-results" role="listbox" aria-label="Matching loans" className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-ink-200 bg-white py-1 shadow-modal" data-id="element-96" data-mp="1i0ead7kbet32">
            {results.length === 0 && <li className="px-3 py-3 text-sm text-ink-500" data-id="element-97" data-mp="lwz1cvdp28k7">
                No loans match “{query}”.
              </li>}
            {results.map(loan => <li key={loan.id} role="option" aria-selected={false} data-id="element-98" data-mp="d3dk95e24f7m">
                <button type="button" onClick={() => {
            onChange(loan.id);
            setOpen(false);
          }} className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-brand-50 focus:bg-brand-50 focus:outline-none" data-id="element-99" data-mp="d1ixdlj86d0w">
                  <FileTextIcon className="h-4 w-4 shrink-0 text-ink-400" data-id="element-100" data-mp="vt23404mw56m" />
                  <span className="min-w-0 flex-1" data-id="element-101" data-mp="qvjr20vbl9ue">
                    <span className="block truncate text-sm font-medium text-ink-900" data-id="element-102" data-mp="qus8bqmtn6lj">
                      {loan.name}
                    </span>
                    <span className="numeric block truncate text-xs text-ink-500" data-id="element-103" data-mp="3bkpz9tbtptod">
                      {loan.loanNumber} · {loan.borrower} ·{' '}
                      {formatCurrency(loan.loanAmount)} · Gross{' '}
                      {formatCurrency(loan.grossCommission)}
                    </span>
                  </span>
                  <CheckIcon className="h-4 w-4 shrink-0 text-transparent" data-id="element-104" data-mp="3beqdi5skx7au" />
                </button>
              </li>)}
          </ul>}
      </div>
    </Field>;
}